/**
 * Resolves the "owner creator" display name for DescribeTab — the name
 * shown in the read-only first creator slot (and used for auto-fill on new
 * items). Three-way priority:
 *
 * 1. Delegation: the delegator's display name (the catalog is theirs;
 *    overriding stored names is correct because the delegate who saved
 *    the record may have left their own name behind).
 * 2. Own add-on catalog: the add-on's `creatorName` (the persona the
 *    user picked for that catalog — e.g. "James Thompson Estate" — not
 *    their primary display name).
 * 3. Otherwise (own primary, or no catalog resolved yet): the user's
 *    display name from `useUserProfile`.
 *
 * `shouldOverrideStoredName` mirrors the old `isDelegatedCatalog` flag:
 * when the catalog has a distinct persona (delegation or own add-on),
 * existing stored `isCurrentUser` creator names are replaced with the
 * resolved persona name. For the primary case we preserve the legacy
 * "only replace invalid names" behaviour by returning false.
 */
export interface ResolveOwnerCreatorNameInput {
  activeDelegation: { delegatorName?: string } | null;
  activeOwnedCatalog: { creatorName: string; isPrimary: boolean } | null;
  userDisplayName: string | null | undefined;
  /**
   * True when the active catalog belongs to a delegator (not the caller).
   * Derived synchronously from `activeCatalogOwnerId`, so it is reliable even
   * before `activeDelegation` (which needs the delegations fetch) has loaded.
   * Used to guard the load-order race below.
   */
  isViewingDelegatedCatalog?: boolean;
  /**
   * Whether the delegations fetch has settled (success or fail). This is the
   * latch that distinguishes "delegations not loaded yet" (defer) from
   * "loaded, but the delegator has no resolvable name" (proceed read-only).
   * Without it, a delegator with no display name — or a revoked catalog still
   * pinned in localStorage — would leave `ready` false forever and deadlock
   * the Describe tab. Defaults to true (own-catalog callers never wait).
   */
  delegationContextLoaded?: boolean;
}

export interface ResolveOwnerCreatorNameResult {
  name: string;
  shouldOverrideStoredName: boolean;
  /**
   * False when the owner-creator name cannot yet be resolved safely (a
   * delegated catalog whose `delegatorName` has not loaded). Callers MUST NOT
   * write the owner-creator slot while `ready` is false — doing so during the
   * race is exactly what stamped the delegate's own name onto the delegator's
   * item. Always true for own catalogs and once the delegator name resolves.
   */
  ready: boolean;
}

export const resolveOwnerCreatorName = ({
  activeDelegation,
  activeOwnedCatalog,
  userDisplayName,
  isViewingDelegatedCatalog = false,
  delegationContextLoaded = true,
}: ResolveOwnerCreatorNameInput): ResolveOwnerCreatorNameResult => {
  if (activeDelegation?.delegatorName) {
    return { name: activeDelegation.delegatorName, shouldOverrideStoredName: true, ready: true };
  }
  // Race guard: we are on a delegated catalog but the delegator's name has not
  // resolved. NEVER fall through to `userDisplayName` here — under a delegation
  // that is the delegate's own name, which must never be written into the
  // owner-creator slot. Defer ONLY while delegations are still loading; once
  // they have settled (the delegator genuinely has no name, or the catalog was
  // revoked), proceed read-only so the tab can never deadlock.
  if (isViewingDelegatedCatalog) {
    return { name: '', shouldOverrideStoredName: false, ready: delegationContextLoaded };
  }
  if (activeOwnedCatalog && !activeOwnedCatalog.isPrimary) {
    return { name: activeOwnedCatalog.creatorName, shouldOverrideStoredName: true, ready: true };
  }
  return { name: userDisplayName ?? '', shouldOverrideStoredName: false, ready: true };
};
